import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ConfigurationService } from '@interacta-shared/data-access-configuration';
import {
    BreakpointService,
    MenuButtonComponent,
    MenuComponent,
    MenuItemDecoratorDirective,
} from '@interacta-shared/ui';
import { IconComponent } from '@interacta-shared/ui-icons';
import {
    CurrentLanguageV2Pipe,
    getLabelServerTranslationV3,
} from '@interacta-shared/util-common';
import { digitalWorkplaceHome } from '@modules/app-routing/routes';
import { CoreDiscoveryService } from '@modules/core/services/core-discovery.service';
import { HomeSelectorItem } from '@modules/header/models/home-selector.model';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';

@Component({
    selector: 'interacta-home-selector',
    templateUrl: './home-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CdkOverlayOrigin,
        CurrentLanguageV2Pipe,
        IconComponent,
        MenuButtonComponent,
        MenuComponent,
        MenuItemDecoratorDirective,
        NgClass,
        TranslatePipe,
    ],
})
export class HomeSelectorComponent {
    private readonly discoveryService = inject(CoreDiscoveryService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly breakpointService = inject(BreakpointService);

    readonly currentHomeId = input.required<number>();
    readonly selectableHomes = input.required<HomeSelectorItem[]>();

    readonly changeCurrentHome = output<number>();

    readonly selectedHome = computed(() => {
        const homes = this.selectableHomes();
        const currentId = this.currentHomeId();
        return homes.find(({ id }) => id === currentId);
    });
    readonly selectedHomeName = computed(() => this.selectedHome()?.name);

    readonly isMd = computed(
        () => !!this.breakpointService.breakpointsSignal()?.isMd,
    );

    readonly isOpen = signal(false);

    constructor() {
        toObservable(this.selectableHomes)
            .pipe(
                takeUntilDestroyed(),
                filter((homes) => homes.length > 1),
                filter((homes) => homes.some(({ isNew }) => isNew)),
                filter(
                    (homes) =>
                        homes.filter(({ isNew }) => isNew)[0].id !==
                        this.currentHomeId(),
                ),
                switchMap((homes) => {
                    const firstNewHome = homes.filter(({ isNew }) => isNew)[0];
                    return this.discoveryService.openTip(
                        { id: 'new-home', instance: firstNewHome.id },
                        {
                            title: 'DISCOVERY.DIGITAL_WORKPLACE.NEW_HOME.TITLE',
                            message:
                                'DISCOVERY.DIGITAL_WORKPLACE.NEW_HOME.DESCRIPTION',
                            translateParams: {
                                home: getLabelServerTranslationV3(
                                    firstNewHome.name,
                                    this.configurationService.currentLanguage()
                                        .code,
                                    true,
                                ),
                            },
                            image: 'gardening',
                            payload: { boundary: digitalWorkplaceHome },
                            direction: 'vertical',
                            tipActions: [
                                {
                                    id: 'newHome',
                                    label: 'DISCOVERY.DIGITAL_WORKPLACE.NEW_HOME.GO_TO_HOME',
                                    data: { id: firstNewHome.id },
                                },
                            ],
                        },
                    );
                }),
                filter(
                    (action) => action !== 'close' && action.id === 'newHome',
                ),
            )
            .subscribe((action) => {
                if (action !== 'close' && action.id === 'newHome') {
                    this.itemSelected(action.data);
                }
            });
    }

    itemSelected(item: Pick<HomeSelectorItem, 'id'>): void {
        this.changeCurrentHome.emit(item.id);
        this.isOpen.set(false);
    }
}
